// utils/pdfExport.ts
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export function generateDeliveryPdf(address: any, rows: any[]) {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;

    // --- Group rows by line_id ---
    const groups = rows.reduce((acc, row) => {
        if (!acc[row.line_id]) acc[row.line_id] = [];
        acc[row.line_id].push(row);
        return acc;
    }, {} as Record<string, any[]>);

    const body: any[] = [];
    const notIncludedSummary: string[] = [];
    const outstandingSummary: string[] = [];

Object.values(groups).forEach((groupRows: any[]) => {
  const first = groupRows[0];
  const qtyOrdered = first.qty_ordered || 0;
  const totalToShip = groupRows.reduce((s, r) => s + (r.qty_to_ship || 0), 0);
  const outstanding = Math.max(qtyOrdered - totalToShip, 0);

  const allNotIncluded = groupRows.every(r => r.not_included);
  const allZeroShip = groupRows.every(r => (r.qty_to_ship || 0) === 0);

  // If all are not included OR all have zero qty_to_ship → skip table, mark as not included
  if (allNotIncluded || allZeroShip) {
    notIncludedSummary.push(
      `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
        first.sku_number ? `(${first.sku_number})` : ""
      } - Qty: ${qtyOrdered}`
    );
    return;
  }

  // Filter out child rows with qty_to_ship = 0 (they’re considered outstanding)
  const shippedRows = groupRows.filter(r => (r.qty_to_ship || 0) > 0 && !r.not_included);

  // Skip if no shipped rows remain
  if (!shippedRows.length) {
    if (outstanding > 0) {
      outstandingSummary.push(
        `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
          first.sku_number ? `(${first.sku_number})` : ""
        } - Qty: ${outstanding}`
      );
    }
    return;
  }

  // Add first (main) row to table
  body.push([
    first?.sku_number,
    `${first?.product_name}`,
    `${first.name ?? ""}${first?.name && first?.number ? " - " : ""}${first?.number ?? ""}`,
    first?.size,
    qtyOrdered,
    totalToShip,
    outstanding,
    first?.box?.label || "",
  ]);

  // Add only shipped child rows (skip zero-qty ones)
  for (let i = 1; i < shippedRows.length; i++) {
    const r = shippedRows[i];
    body.push([
      "",
      "",
      "",
      "",
      "",
      r.qty_to_ship || "",
      "",
      r.box?.label || "",
    ]);
  }

  // Add to outstanding summary if some quantity is left
  if (outstanding > 0) {
    outstandingSummary.push(
      `• ${first.product_name}${first.size ? " - " + first.size : ""} ${
        first.sku_number ? `(${first.sku_number})` : ""
      } - Qty: ${outstanding}`
    );
  }
});

    // --- Header drawn on each page ---
    const drawHeader = () => {
        doc.setFontSize(16).setFont("helvetica", "bold");
        doc.text("Delivery Note", margin, 30);

        doc.setFontSize(10).setFont("helvetica", "bold");
        doc.text("Shipment address", margin, 50);

        const addr = [
            address.name,
            address.address1,
            address.address2,
            `${address.postalCode} ${address.city}`,
            address.country,
        ].filter(Boolean);

        doc.setFont("helvetica", "normal");
        addr.forEach((line, idx) => {
            doc.text(line, margin, 65 + idx * 12);
        });
    };

    // --- Table ---
    autoTable(doc, {
        startY: 65 + 12 * 5 + 20,
        head: [["SKU", "Product Name", 'Name & No.', "Size", "Qty ordered", "Qty to ship", "Outstanding", "Box"]],
        body,
        styles: { fontSize: 10, cellPadding: 3, halign: "center" },
        headStyles: { fillColor: [60, 60, 60], textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: { 0: { halign: "left" }, 1: { halign: "left" } },
        margin: { left: margin, right: margin, top: 100, bottom: 60 },
        didDrawPage: ({ pageNumber }) => {
            drawHeader();
            // temporary footer (will overwrite later)
            doc.setFontSize(9).setFont("helvetica", "normal");
            doc.text(`Page ${pageNumber}`, pageWidth - margin - 60, pageHeight - 20);
        },
    });

    let finalY = (doc as any).lastAutoTable.finalY + 20;

    // --- Render summaries with proper paging ---
    const renderList = (title: string, lines: string[]) => {
        if (!lines.length) return;
        if (finalY > pageHeight - 100) {
            doc.addPage();
            finalY = 150;
        }

        doc.setFont("helvetica", "bold").setFontSize(15);
        doc.text(title, margin, finalY);
        finalY += 16;

        doc.setFont("helvetica", "normal").setFontSize(9);
        lines.forEach(line => {
            if (finalY > pageHeight - 60) {
                doc.addPage();
                drawHeader();
                finalY = 120;
            }
            doc.text(line, margin + 10, finalY);
            finalY += 12;
        });

        finalY += 20;
    };

    renderList("Items Not Included in this shipment:", notIncludedSummary);
    renderList("Outstanding Items:", outstandingSummary);

    // --- Finalize page numbers ---
    const totalPages = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(9).setFont("helvetica", "normal");
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 60, pageHeight - 20);
    }

    return doc;
}


// Attach Ctrl+P shortcut
export function enableDeliveryPdfShortcut(address: any, rows: any[]): void {
    const handler = (e: KeyboardEvent) => {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "p") {
            e.preventDefault();
            const doc = generateDeliveryPdf(address, rows);
            doc.autoPrint();
            window.open(doc.output("bloburl"), "_blank");
        }
    };

    window.removeEventListener("keydown", handler); // ensure only one
    window.addEventListener("keydown", handler);
}